import { useEffect, useState } from "react";
import { Truck, Loader2, RefreshCcw, MapPin, Calendar, Hash, Unlock, ArrowRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import api, { formatEzDate } from "../../lib/api";
import { useCollapse } from "../../lib/cockpitCollapse";
import DocumentChecklist from "./DocumentChecklist";

const STATUS_BADGES = {
    ordered: { label: "Beauftragt", cls: "bg-gray-100 text-gray-800" },
    pickup: { label: "Abholung", cls: "bg-blue-100 text-blue-800" },
    in_transit: { label: "Unterwegs", cls: "bg-amber-100 text-amber-800" },
    delivered: { label: "Geliefert", cls: "bg-green-100 text-green-800" },
};

/**
 * Phase 4.19 — Offene Transporte im Händler-Dashboard.
 * Zeigt alle Transporte mit Status ordered/pickup/in_transit. Klick auf
 * den Eintrag öffnet das zugehörige Inserat im Edit-Modus, wo der
 * Transport-Status weitergepflegt werden kann.
 *
 * Feb-2026 (User-Wunsch 26 May): Pro Eintrag jetzt ZWEI Quick-Actions —
 * „Fahrzeug freigeben" (setzt Inserat zurück auf aktiv + storniert die
 * Käufer-Daten) und „Zur Disposition" (springt direkt in die Dispo-Sektion
 * des Edit-Mode). Außerdem URL-Fix: `/edit-listing/{id}` statt der
 * früheren `/listings/{id}/edit` (Route existierte nicht).
 */
export default function OpenTransportsCard() {
    const [data, setData] = useState({ items: [], by_status: {} });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    // Jun-2026 (User-Wunsch) — einklappbar; Zustand geräteübergreifend gespeichert.
    const [collapsed, toggleCollapsed] = useCollapse("open_transports", true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/dealer/transports/open");
            setData(r.data);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const release = async (listingId) => {
        if (!window.confirm('Fahrzeug wirklich wieder freigeben?\n\nDer Status springt zurück auf "aktiv", Käufer-Stammdaten werden gelöscht.')) return;
        setBusy(`release-${listingId}`);
        try {
            await api.post(`/listings/${listingId}/release`);
            toast.success("Fahrzeug freigegeben — Inserat wieder aktiv.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Freigabe fehlgeschlagen.");
        } finally {
            setBusy(null);
        }
    };

    // Jun-2026 — „neu beauftragt" (noch nicht abgeholt) = Aufmerksamkeit nötig → roter Punkt.
    const newCount = data.by_status?.ordered ?? 0;

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="open-transports-card">
            <div className={`flex items-center justify-between ${collapsed ? "" : "mb-3"}`}>
                <button
                    type="button"
                    onClick={toggleCollapsed}
                    className="flex items-center gap-2 text-left"
                    data-testid="open-transports-toggle"
                    aria-expanded={!collapsed}
                >
                    <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
                    <Truck className="w-5 h-5 text-[#FFCC00]" />
                    <h3 className="font-display font-bold text-base">Offene Transporte</h3>
                    {newCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[#DC2626] text-white rounded-full font-mono-num" data-testid="open-transports-new-badge">
                            {newCount} neu
                        </span>
                    )}
                    {data.count > 0 && (
                        <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[#111827] text-[#FFCC00] rounded-sm font-mono-num">{data.count}</span>
                    )}
                </button>
                <button onClick={load} className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1" data-testid="open-transports-refresh">
                    <RefreshCcw className="w-3 h-3" /> Aktualisieren
                </button>
            </div>

            {!collapsed && (<>
            {/* Status-Counter */}
            <div className="grid grid-cols-3 gap-2 mb-3 text-center" data-testid="open-transports-stats">
                <div className="bg-gray-50 border border-gray-200 rounded-sm py-2">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Beauftragt</div>
                    <div className="text-base font-black font-mono-num mt-0.5">{data.by_status?.ordered ?? 0}</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-sm py-2">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Abholung</div>
                    <div className="text-base font-black font-mono-num mt-0.5 text-blue-700">{data.by_status?.pickup ?? 0}</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-sm py-2">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Unterwegs</div>
                    <div className="text-base font-black font-mono-num mt-0.5 text-amber-700">{data.by_status?.in_transit ?? 0}</div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 justify-center py-6 text-gray-500 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Lade Transporte…
                </div>
            ) : data.items.length === 0 ? (
                <div className="text-sm text-gray-500 py-6 text-center" data-testid="open-transports-empty">
                    Aktuell keine offenen Transporte. Beauftrage einen Transport direkt im Inserat über die Disposition-Sektion.
                </div>
            ) : (
                <ul className="divide-y divide-gray-100 max-h-[28rem] overflow-auto" data-testid="open-transports-list">
                    {data.items.map((t) => {
                        const badge = STATUS_BADGES[t.status] || STATUS_BADGES.ordered;
                        // Feb-2026 (User-Wunsch 27.05.2026) — QR-Code-URL führt
                        // direkt in die Disposition-Sektion des Inserats.
                        const dispoUrl = `${window.location.origin}/edit-listing/${t.listing_id}?focus=disposition`;
                        return (
                            <li key={t.id} className="py-2.5" data-testid={`open-transport-${t.id}`}>
                                <div className="flex items-start gap-2 -mx-2 px-2 py-0.5 rounded-sm hover:bg-gray-50">
                                    <button
                                        type="button"
                                        onClick={() => window.open(`/edit-listing/${t.listing_id}`, "_blank", "noopener,noreferrer")}
                                        className="flex-1 min-w-0 text-left"
                                        data-testid={`open-transport-link-${t.id}`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="text-sm font-bold truncate">{t.vehicle_label || "Fahrzeug"}</div>
                                            <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm shrink-0 ${badge.cls}`}>{badge.label}</span>
                                        </div>
                                        {/* Feb-2026 (User-Wunsch 27.05.2026) — VIN + Farbe als kompakte Identifikatoren */}
                                        {(t.vin || t.color) && (
                                            <div className="text-[11px] text-gray-600 mt-0.5 flex items-center gap-3 flex-wrap" data-testid={`open-transport-id-${t.id}`}>
                                                {t.vin && <span className="inline-flex items-center gap-1 font-mono-num"><Hash className="w-3 h-3" />VIN {t.vin}</span>}
                                                {t.color && <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full border border-gray-300" style={{ background: "#e5e7eb" }} />{t.color}{t.color_finish ? ` (${t.color_finish})` : ""}</span>}
                                            </div>
                                        )}
                                        <div className="text-xs text-gray-600 mt-0.5 flex items-center gap-3 flex-wrap">
                                            <span className="inline-flex items-center gap-0.5"><Truck className="w-3 h-3" />{t.provider}</span>
                                            {t.pickup_address && <span className="inline-flex items-center gap-0.5 truncate"><MapPin className="w-3 h-3" />{t.pickup_address} → {t.dropoff_address || "?"}</span>}
                                            {t.pickup_date && <span className="inline-flex items-center gap-0.5"><Calendar className="w-3 h-3" />{formatEzDate(t.pickup_date)}</span>}
                                            {t.tracking_number && <span className="inline-flex items-center gap-0.5 font-mono-num"><Hash className="w-3 h-3" />{t.tracking_number}</span>}
                                        </div>
                                    </button>
                                    {/* Feb-2026 (User-Wunsch 27.05.2026) — kleiner QR-Code zur Disposition */}
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); window.open(`/edit-listing/${t.listing_id}?focus=disposition`, "_blank", "noopener,noreferrer"); }}
                                        className="shrink-0 p-1 bg-white border border-gray-200 rounded-sm hover:border-[#FFCC00] hover:shadow-sm transition-all"
                                        title="QR-Code zur Disposition · Klick öffnet Inserat"
                                        data-testid={`open-transport-qr-${t.id}`}
                                    >
                                        <QRCodeSVG
                                            value={dispoUrl}
                                            size={44}
                                            level="L"
                                            bgColor="#FFFFFF"
                                            fgColor="#111827"
                                            aria-label="QR-Code zur Disposition"
                                        />
                                    </button>
                                    {/* Feb-2026 (User-Wunsch 26 May): zwei Quick-Actions pro Eintrag */}
                                    <div className="flex flex-col gap-1 shrink-0">
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); window.open(`/edit-listing/${t.listing_id}?focus=disposition`, "_blank", "noopener,noreferrer"); }}
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-[#0EA5E9] text-[#0EA5E9] hover:bg-[#0EA5E9]/10"
                                            title="Direkt in die Disposition-Sektion springen"
                                            data-testid={`open-transport-dispo-${t.id}`}
                                        >
                                            Dispo <ArrowRight className="w-3 h-3" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); release(t.listing_id); }}
                                            disabled={busy === `release-${t.listing_id}`}
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-amber-600 text-amber-700 hover:bg-amber-50 disabled:opacity-60"
                                            title="Fahrzeug freigeben — Status zurück auf aktiv, Käufer-Daten löschen"
                                            data-testid={`open-transport-release-${t.id}`}
                                        >
                                            <Unlock className="w-3 h-3" /> Freigeben
                                        </button>
                                    </div>
                                </div>
                                {/* Feb-2026 — Belegcheck pro verkauftem Fahrzeug + Quick-Upload */}
                                <DocumentChecklist documents={t.documents} listingId={t.listing_id} onChanged={load} />
                            </li>
                        );
                    })}
                </ul>
            )}
            </>)}
        </div>
    );
}
